import React, { useRef, useState } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { APPLICANT_APPLICATION_ROUTE, APPLICANT_WORKSPACE_ROUTE, routeAfterCentralLogin, useAccess } from "../auth/AccessProvider";
import { getSafeAccessError } from "../api/platformAccessApi";
import AccessShell from "../components/AccessShell";

export default function AccessPendingPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, signOutUser, refreshAccessContext, activeTenant, loading } = useAccess();
  const [retryPending, setRetryPending] = useState(false);
  const [retryError, setRetryError] = useState("");
  const retryInFlight = useRef(false);
  const retryAttempt = useRef(0);
  const reason = searchParams.get("reason");
  const copy = reason === "product"
    ? { title: "System access unavailable.", message: "This system is not allocated to your active company or role." }
    : reason === "service"
      ? { title: "Access verification is temporarily unavailable.", message: "We could not reach the central access service. Your access allocations have not been changed." }
    : reason === "session"
      ? { title: "Secure session verification required.", message: "Your current session could not be verified. Try again or sign in once more." }
    : reason === "system-role"
      ? { title: "No systems assigned yet.", message: "Your company account is active, but no system role is currently assigned. Ask your Tenant Administrator to check your product access, then check access again." }
    : reason === "product-role"
      ? { title: "System role unavailable.", message: "Your system role is missing, unsupported, or conflicts with another assigned role. Contact your Tenant Administrator." }
    : reason === "email-verification"
      ? { title: "Verify your email.", message: "Email verification is required before administrator access can be activated." }
      : { title: "Access is pending.", message: "Your identity is signed in, but no active product authority was found." };

  const retry = async () => {
    if (retryInFlight.current) return;
    retryInFlight.current = true;
    const attempt = ++retryAttempt.current;
    setRetryPending(true);
    setRetryError("");
    try {
      const context = await refreshAccessContext({ forceTokenRefresh: true });
      if (attempt !== retryAttempt.current) return;
      if (!context) {
        const interrupted = new Error(
          "Access verification was interrupted. Please try again.",
        );
        interrupted.code = "functions/cancelled";
        throw interrupted;
      }
      navigate(routeAfterCentralLogin(context), { replace: true });
    } catch (error) {
      if (attempt === retryAttempt.current) {
        setRetryError(getSafeAccessError(
          error,
          "We could not verify access right now. Please try again.",
        ));
      }
    } finally {
      if (attempt === retryAttempt.current) {
        retryInFlight.current = false;
        setRetryPending(false);
      }
    }
  };

  const signOut = async () => {
    retryAttempt.current += 1;
    retryInFlight.current = false;
    setRetryPending(false);
    await signOutUser();
    navigate("/login", { replace: true });
  };

  if (!reason) {
    if (loading) return null;
    return (
      <Navigate
        to={activeTenant ? APPLICANT_WORKSPACE_ROUTE : APPLICANT_APPLICATION_ROUTE}
        replace
      />
    );
  }

  return (
    <AccessShell eyebrow="Central access" title={copy.title} description={copy.message} compact>
      <div className="pa-empty-action">
        {user?.email ? <p>Signed in as <strong>{user.email}</strong></p> : <p>You are not currently signed in.</p>}
        {retryError ? (
          <div className="pa-alert is-error pa-access-retry-error" role="alert">
            <span aria-hidden="true">!</span>
            <p>{retryError}</p>
          </div>
        ) : null}
        <button
          aria-busy={retryPending}
          className="pa-primary-button"
          disabled={retryPending}
          type="button"
          onClick={retry}
        >
          <span>{retryPending ? "Verifying access..." : "Check access again"}</span>
          {retryPending ? (
            <span aria-hidden="true" className="pa-button-spinner" />
          ) : <span aria-hidden="true">›</span>}
        </button>
        <button className="pa-text-button" type="button" onClick={signOut}>Sign out</button>
        <Link to="/contactAndUs">Contact access support</Link>
      </div>
    </AccessShell>
  );
}
